import React, { Fragment, useMemo, useState, useEffect } from 'react'
import { useQuery, useMutation } from 'components/graphql'
import { useHistory } from 'react-router'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import getDxColumns from './dxColumns'
import get from 'lodash/get'
import isEqual from 'lodash/isEqual'
import {
	StyledCategoriesTitle,
	StyledSettingsWrapper,
	StyledBlockCategories,
	StyledCategoriesCheckboxes,
	StyledSecondRow,
	StyledTableContainer
} from './styled'
import moment from 'moment'

import ContenHeader from 'components/ContenHeader'
import { ContentBlock, ContentBlockLoading } from 'components/ContentBlock'
import DxDataTable from 'components/DxDataTable'
import Button from 'components/Button'
import { Editing } from 'devextreme-react/data-grid'
import { ServiceModal } from 'components/Modal'
import { DateTimePicker, Checkbox, Number, Text, Select } from 'components/forms'
import { Row, Col } from 'react-flexbox-grid'

import query from 'config/localServer/query/priceLevelsQuery'
import createManySpecialPrices from '../../gql/mutation/createManySpecialPrices'
import updateManyProductPrices from '../../gql/mutation/updateManyProductPrices'
import { listProducts } from '../../gql/query'

import useLocale from 'hooks/useLocale'
import useCategorizedColumn from './hooks/useCategorizedColumn'
import { generateDifferentPrices, generateOnEditorPreparing } from './utils'

const AutomaticPrice = () => {
	const [t] = useTranslation()
	const history = useHistory()
	const { id: recordId } = useParams()
	const { data } = useQuery(query, {
		fetchPolicy: 'cache-and-network'
	})
	const locale = useLocale()
	const dateNow = moment().utc().startOf('day').format()

	const selectOptions = [
		{ value: 2, label: '.01' },
		{ value: 1, label: '.1' },
		{ value: null, label: '.05', toNearest: true }
	]

	const [selectVal, setSelectVal] = useState(selectOptions[0])
	const [displayedColumn, setDisplayedColumn] = useState({})
	const [percentValue, setPercentValue] = useState(null)
	const [datePeriod, setDatePeriod] = useState(dateNow)
	const [visibleCols, setVisibleCols] = useState([])
	const [pricePosition, setPricePosition] = useState([])
	const [priceInCafePosition, setPriceInCafePosition] = useState([])
	const [commentValue, setCommentValue] = useState('')
	const [selectRowManually, setSelectRowManually] = useState([])
	const [tableData, setTableData] = useState([])
	const [showModal, setShowModal] = useState(false)
	const [canUpdate, setCanUpdate] = useState(false)

	const listProductsVariables = { variables: { filter: { includeWawiProducts: false, includeCashassistProducts: true, isActive: true } } }
	const { data: productsData, loading } = useQuery(listProducts, listProductsVariables)
	const listProductsData = get(productsData, 'listProducts.items', [])
	const copyListProductsData = JSON.parse(JSON.stringify(listProductsData))

	const handleReset = () => {
		setPricePosition([])
		setPriceInCafePosition([])
		setPercentValue(null)
		setDatePeriod(dateNow)
		setSelectRowManually([])
		setCommentValue('')
		setTableData(copyListProductsData)
	}

	const [createManyPrices] = useMutation(createManySpecialPrices, {
		onCompleted: handleReset
	})
	const [updateManyPrices] = useMutation(updateManyProductPrices, {
		onCompleted: handleReset
	})

	const priceCategories = get(data, 'getClientPriceLevels', [])
	const priceInCafeCategories = []
	priceCategories.forEach((name) => {
		priceInCafeCategories.push(`${name} Im Haus`)
	})
	const categories = [...priceCategories, ...priceInCafeCategories]

	const categorizedPriceColumn = useCategorizedColumn(priceCategories, pricePosition, setPricePosition, visibleCols, displayedColumn, tableData, t, 'price', locale)
	const categorizedInCafeColumn = useCategorizedColumn(priceInCafeCategories, priceInCafePosition, setPriceInCafePosition, visibleCols, displayedColumn, tableData, t, 'priceInCafe', locale)

	const dxColumns = useMemo(() => {
		const filteredColumns = getDxColumns(t, [...categorizedPriceColumn, ...categorizedInCafeColumn])
		return filteredColumns
	}, [categorizedPriceColumn, categorizedInCafeColumn])

	const handleShowActiveColumns = (e) => {
		const currentName = e.target.name
		setDisplayedColumn({ ...displayedColumn, [currentName]: !displayedColumn[currentName] })
	}
	const onSelectionChanged = (e) => {
		setSelectRowManually(e.selectedRowsData)
	}
	const isFutureDate = datePeriod > dateNow

	const handleSaveRows = (e) => {
		setSelectRowManually((prev) => {
			return [...prev.filter(({ id }) => id !== e.changes[0].key.id), e.changes[0].key]
		})
	}

	const handleRecalculatePrices = () => {
		const tableValues = tableData.map((product) => {
			const priceResult = generateDifferentPrices(pricePosition, product, 'newPrice', percentValue, selectVal)
			const priceInCafeResult = generateDifferentPrices(priceInCafePosition, priceResult, 'newPriceInCafe', percentValue, selectVal)
			return priceInCafeResult
		})
		setTableData(tableValues)
		setPricePosition([])
		setPriceInCafePosition([])
	}

	const addNecessaryFields = (obj) => {
		if(isFutureDate) {
			return { ...obj, comment: commentValue, date: datePeriod }
		}
		return obj
	}

	const updateValuesMutation = () => {
		const idArr = []
		const products = selectRowManually.map(item => {
			idArr.push(item.id)
			return addNecessaryFields({
				id: item.id,
				prices: item.newPrice,
				pricesInCafe: item.newPriceInCafe
			})
		})
		const variables = isFutureDate ? { newPrices: products } : { idArr, updateValues: products }
		return isFutureDate ? createManyPrices({ variables, refetchQueries: [{ query: listProducts, ...listProductsVariables }] }) : updateManyPrices({ variables, refetchQueries: [{ query: listProducts, ...listProductsVariables }] })
	}
	const toggleModal = () => setShowModal(prev => !prev)

	useEffect(() => {
		if(!loading) {
			setTableData(copyListProductsData)
		}
	}, [listProductsData])

	useEffect(() => {
		selectRowManually.forEach((item) => {
			if('newPrice' in item || 'newPriceInCafe' in item) {
				setCanUpdate(true)
			} else {
				setCanUpdate(false)
			}
		})
	}, [selectRowManually])

	useEffect(() => {
		setVisibleCols(displayedColumn)
	}, [displayedColumn])

	const isChangedData = isEqual(tableData, listProductsData)

	return (
		<Fragment>
			<ContenHeader title={t('common.strings.automatic_price_adjustment')} />
			<ContentBlock>
				<Row>
					<Col xs>
						<StyledCategoriesTitle>{t('options_section.fields.price_categories')}</StyledCategoriesTitle>
						<StyledSettingsWrapper>
							<Row style={{ marginLeft: '0px' }}>
								<Col>
									<StyledBlockCategories>
										<StyledCategoriesCheckboxes>
											{categories.map(item => <Checkbox key={item} disabled={loading} name={item} noForm checked={!!displayedColumn[item]} value={displayedColumn[item]} onChange={handleShowActiveColumns} inline label={item}/>)}
										</StyledCategoriesCheckboxes>
									</StyledBlockCategories>
								</Col>
								<Col className='recalculateSettings' xs={8}>
									<Row>
										<DateTimePicker
											style={{ width: '150px' }}
											value={datePeriod}
											picker='date'
											onChange={setDatePeriod}
											noForm
											disablePast
											label={t('common.fields.from_date')}
											inline
										/>
										<Number
											style={{ width: '150px' }}
											noForm
											value={percentValue}
											enableNegative={true}
											onChange={setPercentValue}
											label={t('product_section.fields.price_adjustment')}
											inline
											placeholder={t('category_section.placeholders.position')}
										/>
										<Select
											noForm
											options={selectOptions}
											style={{ width: '300px' }}
											label={t('options_section.fields.currency_round_to')}
											onChange={setSelectVal}
											value={selectVal}
											simpleValue={false}
										/>
									</Row>
									<Row>
										<StyledSecondRow>
											<Text
												className='commentInput'
												noForm
												value={commentValue}
												onChange={(e) => setCommentValue(e.target.value)}
												inline
												disabled={!isFutureDate}
												label={t('product_section.strings.comment')}
											/>
											<Button
												onClick={handleRecalculatePrices}
												className='calculateButton'
												status={percentValue ? 'active' : 'inactive'}
												text={t('common.buttons.calculate')}
											/>
										</StyledSecondRow>
									</Row>
								</Col>
							</Row>
						</StyledSettingsWrapper>
					</Col>
				</Row>
				<ContentBlockLoading
					isLoading={loading}
					style={{ backgroundColor: '#F4F4F4', width: '820px', boxShadow: 'none', padding: '0px', background: '#F4F4F4' }}>
					<StyledTableContainer bgColor='#fffb0042'>
						<DxDataTable
							data={tableData}
							onSaving={handleSaveRows}
							onEditorPreparing={(e) => generateOnEditorPreparing(e, locale)}
							withSearch={false}
							columns={dxColumns}
							height={508}
							style={{ width: dxColumns.length > 4 ? '1220px' : '820px' }}
							onSelectionChanged={onSelectionChanged}
							selectedRowKeys={selectRowManually}
						>
							<Editing
								mode='cell'
								allowUpdating={true}
							/>
						</DxDataTable>
					</StyledTableContainer>
				</ContentBlockLoading>
				<Row style={{ justifyContent: 'end', marginTop: '40px' }}>
					<Button
						onClick={() => {
							handleReset()
							history.push(`/artikel${recordId ? '/' + recordId : ''}`)
						}}
						className='calculateButton'
						status='primary'
						text={t('common.buttons.abort')}
					/>
					<Button
						onClick={toggleModal}
						className='calculateButton'
						status={selectRowManually.length && !isChangedData && canUpdate ? 'active' : 'inactive'}
						text={t('common.buttons.save')}
					/>
					{showModal &&
					<ServiceModal
						onClose={toggleModal}
						label={t('common.buttons.confirm')}
						btnText={t('common.buttons.confirm')}
						btnCancelText={t('common.buttons.cancel')}
						text={t('common.strings.product_change_confirmation', { count: selectRowManually.length })}
						onOk={() => {
							updateValuesMutation()
							setShowModal(false)
						}}
						width={1200}>
						<div style={{ marginBottom: '40px', marginLeft: '7px' }}>
							<StyledTableContainer bgColor='#fffb0042'>
								<DxDataTable
									data={selectRowManually}
									withSearch={false}
									columns={dxColumns}
									height={350}
									width={1150}
							 />
							</StyledTableContainer>
						</div>
					</ServiceModal>
					}
				</Row>
			</ContentBlock>
		</Fragment>
	)
}

export default AutomaticPrice
